import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { getMonthDateRange } from './datePickerUtils';
import { datepickerStyles, getHighlightedDates, Legend } from './Legend';
import { colors } from 'packages/common-frontend/colors';
import { useGetBorrowerPipelineRunsQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { RDFV } from 'packages/dataPipeline/types/dataPipelineTypes';

export const CalendarModal = ({
  selectedRDFV,
  setSelectedRDFV,
  onClose,
  borrowerOrganizationId,
}: {
  selectedRDFV: RDFV | null;
  setSelectedRDFV: (pipeline: RDFV | null) => void;
  onClose: () => void;
  borrowerOrganizationId: string;
}) => {
  const buttonColorScheme = useColorModeValue('aurora', 'gray');
  const bgColor = useColorModeValue(colors.background[50], 'gray.800');
  const [selectedDate, setSelectedDate] = useState<Util.FullDateStr | null>(
    selectedRDFV?.reportingDate || null,
  );
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);

  // versions is a map of reporting dates to the max version for that date
  // anytime we select a new month, we will fetch the versions for that month
  // and fill this map. it will be set to null when the query for the versions
  // is pending
  const [versions, setVersions] = useState<Map<
    Util.FullDateStr,
    number
  > | null>(null);

  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  useEffect(() => {
    if (selectedRDFV) {
      setSelectedDate(selectedRDFV.reportingDate);
      setSelectedVersion(selectedRDFV.fileVersion);
      setCurrentMonth(commonDate.fullDateStrToDate(selectedRDFV.reportingDate));
      setVersions(null);
    }
  }, [selectedRDFV]);

  const { data, error, refetch } = useGetBorrowerPipelineRunsQuery({
    variables: {
      borrowerOrganizationId,
      startDate: getMonthDateRange(currentMonth).startDate,
      endDate: getMonthDateRange(currentMonth).endDate,
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data && data.getPipelineRuns) {
      const maxVersionByDate = new Map<Util.FullDateStr, number>();
      data.getPipelineRuns.forEach(
        (run: { reportingDate: Util.FullDateStr; fileVersion: number }) => {
          const maxVersion = maxVersionByDate.get(run.reportingDate) || 0;
          maxVersionByDate.set(
            run.reportingDate,
            Math.max(maxVersion, run.fileVersion),
          );
        },
      );
      setVersions(maxVersionByDate);
    } else {
      setVersions(null);
    }
  }, [data]);

  const handleConfirm = useCallback(() => {
    if (selectedDate !== null && selectedVersion !== null && versions) {
      setSelectedRDFV({
        reportingDate: selectedDate,
        fileVersion: selectedVersion,
      });
      onClose();
    }
  }, [versions, onClose, selectedDate, selectedVersion, setSelectedRDFV]);
  const canSelect =
    selectedDate !== null && selectedVersion !== null && versions;

  const handleMonthChange = (date: Date) => {
    setCurrentMonth(date);
    setVersions(null);
    refetch({
      borrowerOrganizationId,
      startDate: getMonthDateRange(date).startDate,
      endDate: getMonthDateRange(date).endDate,
    });
  };

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxWidth="fit-content" bg={bgColor}>
        <ModalHeader>Select Reporting Date</ModalHeader>

        <ModalBody>
          <Legend />
          <Box
            sx={{
              // custom styles for highlighted dates.
              ...datepickerStyles,

              // styles below control the size of the datepicker
              '.react-datepicker': {
                fontSize: '1.2rem',
              },
              '.react-datepicker__day, .react-datepicker__day-name, .react-datepicker__day-number': {
                width: '3rem',
                height: '3rem',
                lineHeight: '3rem',
                margin: '0.2rem',
              },
              '.react-datepicker__header': {
                paddingTop: '1rem',
                paddingBottom: '1rem',
              },
            }}
            width="100%"
          >
            <ReactDatePicker
              selected={
                selectedDate ? commonDate.fullDateStrToDate(selectedDate) : null
              }
              onChange={(date: Date) => {
                if (versions) {
                  const newSelectedDate = commonDate.dateToFullDateString(date);
                  setSelectedDate(newSelectedDate);
                  const currentMax = versions.get(newSelectedDate) || 0;
                  setSelectedVersion(currentMax + 1);
                }
              }}
              highlightDates={getHighlightedDates({
                selectedDate,
                versions, // when versions is null(i.e. loading) we will show all dates as non-selectable
                currentMonth,
              })}
              inline
              monthsShown={1}
              onMonthChange={handleMonthChange} // Use the handleMonthChange function
            />
          </Box>
          {error && <Text color="red.500">Error loading versions</Text>}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button
              colorScheme={buttonColorScheme}
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              isDisabled={!canSelect}
              colorScheme={buttonColorScheme}
              onClick={handleConfirm}
            >
              Select
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
